import { FeatureFlagClient, FeatureFlagValue } from "../FeatureFlagClient"

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    posthog?: any
  }
}

export class PostHogWebClient implements FeatureFlagClient {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private posthog: any = null
  private ready = false
  private callbacks: (() => void)[] = []

  async init(apiKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if running on server-side
      if (typeof window === "undefined") {
        resolve()
        return
      }

      // Get configuration from environment variables
      const apiHost = process.env.NEXT_PUBLIC_POSTHOG_API_HOST

      // Load PostHog script
      const script = document.createElement("script")
      script.src = "https://app.posthog.com/static/array.js"

      script.onload = () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        window.posthog?.init(apiKey, {
          api_host: apiHost,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          loaded: (posthog: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.posthog = posthog

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            posthog.onFeatureFlags(() => {
              this.ready = true
              this.callbacks.forEach((callback) => callback())
              this.callbacks = []
            })

            resolve()
          },
        })
      }

      script.onerror = () => {
        console.error("PostHog initialization failed")
        reject(new Error("Failed to load PostHog script"))
      }

      document.head.appendChild(script)
    })
  }

  isEnabled(flagKey: string): boolean {
    if (!this.posthog || !this.ready) return false
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return this.posthog.isFeatureEnabled(flagKey) === true
  }

  getVariant(flagKey: string): FeatureFlagValue {
    if (!this.posthog || !this.ready) return false
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.posthog.getFeatureFlag(flagKey)
  }

  onFlagsLoaded(callback: () => void): void {
    if (this.ready) {
      callback()
    } else {
      this.callbacks.push(callback)
    }
  }

  async reload(): Promise<void> {
    if (this.posthog) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      await this.posthog.reloadFeatureFlags()
    }
  }

  identify(userId: string, properties: Record<string, unknown> = {}): void {
    if (this.posthog) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.posthog.identify(userId, properties)
    }
  }

  shutdown(): void {
    if (this.posthog) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.posthog.reset()
    }
    this.ready = false
    this.callbacks = []
  }

  isReady(): boolean {
    return this.ready
  }
}
